import {type AnalyticsBrowser} from '@dreamdata/analytics-next'
import {useState, useEffect, useRef, useCallback} from 'react'
import {DREAM_DATA_KEY} from 'src/common/constants'

type Trackers = {
  loading: boolean
  error: Error | null
  getActiveTracker: (hasConsent: boolean) => AnalyticsBrowser | null
}

export const useGetTrackers = (): Trackers => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const isMountedRef = useRef(true)
  const currentConsentRef = useRef<boolean | null>(null)
  const activeTrackerRef = useRef<AnalyticsBrowser | null>(null)
  const analyticsBrowserRef = useRef<typeof import('@dreamdata/analytics-next') | null>(null)

  // Initialize the analytics library (not the trackers)
  useEffect(() => {
    const loadAnalyticsLibrary = async () => {
      try {
        if (!DREAM_DATA_KEY) {
          throw new Error('DREAM_DATA_KEY is not defined')
        }

        // Load the library once
        analyticsBrowserRef.current = require('@dreamdata/analytics-next')

        if (isMountedRef.current) {
          setLoading(false)
        }
      } catch (err) {
        console.error('Failed to load DreamData analytics library:', err)

        if (isMountedRef.current) {
          setError(err instanceof Error ? err : new Error('Unknown error loading analytics library'))
          setLoading(false)
        }
      }
    }

    loadAnalyticsLibrary()

    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Lazy-load tracker based on consent
  const getActiveTracker = useCallback(
    (hasConsent: boolean): AnalyticsBrowser | null => {
      if (loading || error || !analyticsBrowserRef.current) {
        return null
      }

      // If consent status hasn't changed and we have a tracker, return it
      if (currentConsentRef.current === hasConsent && activeTrackerRef.current) {
        return activeTrackerRef.current
      }

      try {
        // Consent status changed - cleanup previous tracker if it exists
        if (activeTrackerRef.current && currentConsentRef.current !== null) {
          activeTrackerRef.current = null
        }

        const newTracker: AnalyticsBrowser = analyticsBrowserRef.current.AnalyticsBrowser.load(
          {writeKey: DREAM_DATA_KEY},
          hasConsent ? {} : {anonymous: true},
        )

        // Update refs
        currentConsentRef.current = hasConsent
        activeTrackerRef.current = newTracker

        return newTracker
      } catch (err) {
        console.error(`Failed to initialize ${hasConsent ? 'cookie' : 'cookieless'} tracker:`, err)
        return null
      }
    },
    [loading, error],
  )

  return {loading, error, getActiveTracker}
}
